/**
 * Route Tracking ▸ **Route map** (prototype v68).
 *
 * The stops of one route, geocoded from their site addresses and pinned in
 * visit order, so a dispatcher can see whether a round is a sensible drive
 * rather than reading nine addresses and picturing it. Shown beside the stop
 * list — in the route's detail and in the Routes list's Map dialog.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ⚠ THIS COMPONENT SENDS SITE ADDRESSES TO A THIRD PARTY.
 *
 * Geocoding asks OpenStreetMap's Nominatim service "where is this address",
 * which means the CLIENT'S SITE ADDRESS leaves the lab's network. Map tiles are
 * likewise fetched from openstreetmap.org, which reveals the AREA being looked
 * at. For a lab handling regulated work that is a decision for the lab to make,
 * not one to discover — so:
 *
 *   · both endpoints are the constants below and nowhere else, so pointing
 *     them at a self-hosted Nominatim / tile server is a one-line change;
 *   · nothing is sent until the user OPENS a route map — nothing pre-fetches,
 *     and the list views never geocode;
 *   · results are cached per address for the session, so an address is looked
 *     up once rather than on every render.
 *
 * See ADR-0010.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * ADDRESSES THE MAP FILES UNDER ANOTHER NAME. A rural US address's postal city
 * is often not the map's locality: "24782 Mosier Rd, Rainier, OR 97048" is on
 * OpenStreetMap, but under Alston — so free text naming Rainier matched nothing
 * and the stop landed on Rainier's town centre, 10 km off. Each address is
 * therefore tried at falling precision — as written; street + ZIP with the city
 * left out; then without the house number; then just its locality — and a stop
 * placed by a fallback is drawn as an APPROXIMATE pin and counted as such in the
 * footer. One unknown address used to take the whole map down with a message
 * blaming the network.
 *
 * HONEST FAILURE. "Not found" and "could not reach the service" are different
 * problems with different fixes, so they get different messages. Neither is
 * ever an unexplained grey box, and the stop list beside the map is unaffected.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './clientRequest.css';
import type { RouteStopRow } from './clientRequestApi';

/** Where addresses are resolved. Swap for a self-hosted Nominatim to keep
 *  client addresses inside the lab's network. */
const GEOCODE_URL = 'https://nominatim.openstreetmap.org/search';
/** The labs this serves are US labs; restricting the country stops a street
 *  name matching its namesake on another continent. */
const GEOCODE_COUNTRY = 'us';
/** Where map tiles come from. Swap for a self-hosted tile server likewise. */
const TILE_URL = 'https://tile.openstreetmap.org/{z}/{x}/{y}.png';
const TILE_ATTRIB = '© OpenStreetMap contributors';

/** Nominatim asks for no more than one request per second. A condition of
 *  use, not an optimisation — and enforced ACROSS every map on the page (the
 *  dialog and the detail can both be geocoding), not per map. */
const GEOCODE_GAP_MS = 1100;

type Precision = 'exact' | 'street' | 'area';
interface Hit { lat: number; lon: number; precision: Precision }
/** A placed stop. `n` is its position in the visit order passed in, so the pin
 *  carries the same number as the stop list even when a stop cannot be placed. */
interface Located extends Hit { stop: RouteStopRow; n: number }

/** Session cache, keyed by the address text. `null` = looked up at every
 *  precision and not found — a re-render must never re-ask. */
const geoCache = new Map<string, Hit | null>();

/** Two stops can geocode to the IDENTICAL point — two different house numbers
 *  on a street OpenStreetMap only knows at street level both fall back to the
 *  same street centroid (confirmed: "101" and "103 Industrial Way, Houston TX"
 *  both resolve to one point, since neither house number exists as its own
 *  node). Plotted as-is, the later marker sits exactly on top of the earlier
 *  one and hides it completely — a dispatcher sees one pin for a three-stop
 *  route and nothing to click for the other two.
 *
 *  Fans coincident pins out around their shared point in a small ring, wide
 *  enough to separate at street zoom, small enough to still read as "same
 *  place" — the map already marks them approximate, so a few metres of spread
 *  adds no false precision. Distinct coordinates are returned untouched. */
function spreadCoincident<T extends Hit>(pts: T[]): (T & { dlat: number; dlon: number })[] {
  const groups = new Map<string, T[]>();
  for (const p of pts) {
    const key = `${p.lat.toFixed(5)},${p.lon.toFixed(5)}`;
    (groups.get(key) ?? groups.set(key, []).get(key)!).push(p);
  }
  const out: (T & { dlat: number; dlon: number })[] = [];
  for (const group of groups.values()) {
    if (group.length === 1) {
      out.push({ ...group[0]!, dlat: group[0]!.lat, dlon: group[0]!.lon });
      continue;
    }
    // ~25 m radius — enough to separate at the close-up zoom a fully-
    // coincident cluster is given below, still clearly one spot.
    const radius = 0.00025;
    group.forEach((p, i) => {
      const angle = (2 * Math.PI * i) / group.length;
      out.push({ ...p, dlat: p.lat + radius * Math.cos(angle), dlon: p.lon + radius * Math.sin(angle) });
    });
  }
  return out;
}

/** The service could not be asked at all (network, CORS, HTTP error) — as
 *  opposed to answering "no match", which is a different problem. */
class GeocodeUnavailable extends Error {}

let lastCall = 0;
async function throttle(): Promise<void> {
  const wait = lastCall + GEOCODE_GAP_MS - Date.now();
  if (wait > 0) await new Promise(r => setTimeout(r, wait));
  lastCall = Date.now();
}

/** One Nominatim search: free text (`q`) or structured (`street` /
 *  `postalcode` / `state`) — the two cannot be mixed in one request. */
type Query = Record<string, string>;

async function lookup(params: Query): Promise<{ lat: number; lon: number } | null> {
  await throttle();
  const qs = Object.entries(params)
    .map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join('&');
  const url = `${GEOCODE_URL}?format=json&limit=1&countrycodes=${GEOCODE_COUNTRY}&${qs}`;
  let res: Response;
  try {
    res = await fetch(url, { headers: { Accept: 'application/json' } });
  } catch {
    throw new GeocodeUnavailable('no network');
  }
  if (!res.ok) throw new GeocodeUnavailable(`HTTP ${res.status}`);
  const rows = (await res.json()) as Array<{ lat: string; lon: string }>;
  return rows[0] ? { lat: Number(rows[0].lat), lon: Number(rows[0].lon) } : null;
}

/** The address at falling precision:
 *   1. as written (free text);
 *   2. the street line + ZIP (+ state) as a STRUCTURED query, the city left
 *      out — a US postal city is often not the map's locality (24782 Mosier Rd
 *      is "Rainier, OR 97048" to the post office but filed under Alston on
 *      OpenStreetMap, so every lookup naming Rainier missed the house);
 *   3. the same without the house number;
 *   4. the street without the number, free text;
 *   5. just the locality (everything after the street). */
function candidates(address: string): Array<{ params: Query; precision: Precision }> {
  const a = address.trim().replace(/\s+/g, ' ');
  const out: Array<{ params: Query; precision: Precision }> = [{ params: { q: a }, precision: 'exact' }];
  const NUMBER = /^\d+[A-Za-z]?(?:-\d+)?\s+/;
  const comma = a.indexOf(',');
  const streetLine = comma > 0 ? a.slice(0, comma).trim() : '';
  const tail = comma > 0 ? a.slice(comma + 1) : '';
  const zip = tail.match(/\b(\d{5})(?:-\d{4})?\b/)?.[1];
  const state = tail.match(/\b([A-Z]{2})\b(?=\s+\d{5}|\s*$)/)?.[1];
  const place = (street: string): Query =>
    ({ street, postalcode: zip!, ...(state ? { state } : {}) });
  if (streetLine && zip) {
    out.push({ params: place(streetLine), precision: NUMBER.test(streetLine) ? 'exact' : 'street' });
    const bare = streetLine.replace(NUMBER, '');
    if (bare !== streetLine) out.push({ params: place(bare), precision: 'street' });
  }
  const noNumber = a.replace(NUMBER, '');
  if (noNumber !== a) out.push({ params: { q: noNumber }, precision: 'street' });
  if (comma > 0) {
    const locality = tail.trim();
    if (locality) out.push({ params: { q: locality }, precision: 'area' });
  }
  return out;
}

async function geocode(address: string): Promise<Hit | null> {
  const key = address.trim().toLowerCase();
  if (geoCache.has(key)) return geoCache.get(key) ?? null;
  for (const c of candidates(address)) {
    const hit = await lookup(c.params);
    if (hit) {
      const placed: Hit = { ...hit, precision: c.precision };
      geoCache.set(key, placed);
      return placed;
    }
  }
  geoCache.set(key, null);
  return null;
}

type Phase = 'idle' | 'locating' | 'ready' | 'notfound' | 'unavailable';

export default function RouteMap({ stops }: { stops: RouteStopRow[] }) {
  const [phase, setPhase] = useState<Phase>('idle');
  const [located, setLocated] = useState<Located[]>([]);
  const [reason, setReason] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const hostRef = useRef<HTMLDivElement | null>(null);

  const addressed = stops.filter(s => (s.siteAddress ?? '').trim() !== '');
  // Re-geocode when the stops OR their addresses change, not on every render.
  const stopsKey = stops.map(s => `${s.id}:${s.siteAddress ?? ''}`).join('|');

  useEffect(() => {
    if (addressed.length === 0) { setPhase('idle'); return; }
    let alive = true;
    setPhase('locating');
    setReason(null);
    (async () => {
      const found: Located[] = [];
      let unavailable: string | null = null;
      for (const [i, stop] of stops.entries()) {
        if (!alive) return;
        if (!(stop.siteAddress ?? '').trim()) continue;
        try {
          const hit = await geocode(stop.siteAddress!);
          if (hit) found.push({ ...hit, stop, n: i + 1 });
        } catch (e) {
          // The service is out: asking again for each remaining stop would
          // only make the user wait longer for the same answer.
          unavailable = e instanceof GeocodeUnavailable ? e.message : 'unexpected error';
          break;
        }
      }
      if (!alive) return;
      setLocated(found);
      if (found.length) setPhase('ready');
      else if (unavailable) { setReason(unavailable); setPhase('unavailable'); }
      else setPhase('notfound');
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stopsKey, attempt]);

  /* Build the map once the coordinates exist; tear it down on unmount —
     Leaflet holds listeners on the container, and dropping the node without
     remove() leaks them for the life of the tab. */
  useEffect(() => {
    if (phase !== 'ready' || !hostRef.current || located.length === 0) return;
    const map = L.map(hostRef.current, { scrollWheelZoom: false });
    L.tileLayer(TILE_URL, { attribution: TILE_ATTRIB, maxZoom: 19 }).addTo(map);

    // Coincident stops (same address, or two addresses OSM can only place at
    // the same street-level point) are fanned out a few metres so every pin
    // stays visible and clickable — see spreadCoincident().
    const display = spreadCoincident(located);

    display.forEach(p => {
      // A numbered pin, because the ORDER is the point of the picture. An
      // approximate one is drawn hollow, so nobody drives to a town centre
      // thinking it is the door.
      const cls = `rt-pin${p.stop.completedAt ? ' done' : ''}${p.precision !== 'exact' ? ' approx' : ''}`;
      const icon = L.divIcon({
        className: 'rt-pin-wrap',
        html: `<span class="${cls}">${p.n}</span>`,
        iconSize: [26, 26],
        iconAnchor: [13, 13],
      });
      const approx = p.precision === 'exact' ? ''
        : `<br><i>Approximate — placed by ${p.precision === 'street' ? 'street' : 'town'}; `
          + 'the exact address is not on OpenStreetMap.</i>';
      L.marker([p.dlat, p.dlon], { icon })
        .addTo(map)
        .bindPopup(`<b>${p.stop.serviceNo ?? ''}</b><br>${p.stop.clientName ?? ''}<br>`
                 + `${p.stop.siteAddress ?? ''}${approx}`);
    });

    if (located.length > 1) {
      L.polyline(display.map(p => [p.dlat, p.dlon] as [number, number]),
        { color: '#e8670a', weight: 3, opacity: 0.7, dashArray: '6 6' }).addTo(map);
    }
    // A scale bar, as v68 has — at street level it is the only sense of distance.
    L.control.scale({ metric: true, imperial: true }).addTo(map);

    // Several stops that all geocoded to the SAME point (see spreadCoincident)
    // are, for zoom purposes, one location — fitBounds on a near-zero box
    // would still cap out at the wide multi-stop zoom below and the fan-out
    // would be imperceptible. Close in on them the way a single stop does.
    const allCoincident = located.length > 1
      && new Set(located.map(p => `${p.lat.toFixed(5)},${p.lon.toFixed(5)}`)).size === 1;

    // ONE stop, or several that all landed on one point: open at STREET level
    // (v68 lands there too — fitting a single point zooms all the way in). A
    // town-level view of one pin shows nothing a dispatcher can drive by. An
    // approximate pin gets one step less, and a fanned-out coincident cluster
    // one step less again so every spread pin — not just the centre one —
    // stays inside the frame. Several DISTINCT stops: fit them all.
    const fit = () => {
      if (located.length === 1 || allCoincident) {
        const centre = located[0]!;
        const zoom = centre.precision !== 'exact' ? 17 : allCoincident ? 18 : 19;
        return map.setView([centre.lat, centre.lon], zoom);
      }
      return map.fitBounds(
        L.latLngBounds(located.map(p => [p.lat, p.lon] as [number, number])),
        { padding: [30, 30], maxZoom: located.every(p => p.precision === 'exact') ? 15 : 13 });
    };
    fit();
    // Inside a dialog the container can still be settling when the map is
    // built; re-measuring on the next tick keeps tiles from rendering short.
    const t = window.setTimeout(() => { map.invalidateSize(); fit(); }, 60);

    return () => { window.clearTimeout(t); map.remove(); };
  }, [phase, located]);

  const retry = useCallback(() => {
    // A retry must actually ask again — drop this route's "not found" entries.
    for (const s of stops) {
      const k = (s.siteAddress ?? '').trim().toLowerCase();
      if (k && geoCache.get(k) === null) geoCache.delete(k);
    }
    setAttempt(a => a + 1);
  }, [stops]);

  if (addressed.length === 0) {
    return (
      <div className="rt-mapbox">
        <div className="rt-mapmsg">
          <i className="ti ti-map-pin" />
          <b>Nothing to map yet</b>
          <span>Stops need a site address before they can be placed.</span>
        </div>
      </div>
    );
  }

  if (phase === 'locating' || phase === 'idle') {
    return (
      <div className="rt-mapbox">
        <div className="rt-mapmsg">
          <i className="ti ti-loader-2 rt-spin" />
          <b>Locating addresses…</b>
          <span>Looking up {addressed.length} stop{addressed.length > 1 ? 's' : ''} on OpenStreetMap.</span>
        </div>
      </div>
    );
  }

  if (phase === 'notfound' || phase === 'unavailable') {
    return (
      <div className="rt-mapbox">
        <div className="rt-mapmsg">
          <i className="ti ti-map-off" />
          {phase === 'notfound' ? (
            <>
              <b>Addresses not on the map</b>
              <span>
                OpenStreetMap has none of these addresses — not by the address, the
                street or the town. Check the Where on each service. The stop order
                is unaffected.
              </span>
            </>
          ) : (
            <>
              <b>Map unavailable</b>
              {/* The reason is named: "unavailable" alone leaves the dispatcher
                  guessing whether it is their network or the service. */}
              <span>
                The mapping service could not be reached ({reason}) — usually no
                network, or it is blocked from here. The stop order is unaffected.
              </span>
            </>
          )}
          <button className="ca-btn" onClick={retry}>
            <span className="tb-i"><i className="ti ti-refresh" /></span> Try again
          </button>
        </div>
      </div>
    );
  }

  const approx = located.filter(p => p.precision !== 'exact').length;
  return (
    <div className="rt-mapbox">
      <div ref={hostRef} className="rt-mapcanvas" />
      <div className="rt-mapfoot">
        <i className="ti ti-map-pin" />
        {located.length} of {addressed.length} stop{addressed.length > 1 ? 's' : ''} placed
        {approx > 0 && <> · {approx} approximate</>} · pins in visit order ·
        map data © OpenStreetMap contributors
      </div>
    </div>
  );
}
